/*eslint max-lines-per-function: ["error", 200]*/
import React, { Dispatch, SetStateAction, useEffect, useState } from 'react';
import { Form } from 'react-bootstrap';
import { isEqual } from 'lodash';

import {
  CreateUpdateQuestionDto,
  GetSurveyDto,
  ResponseError,
} from '../../../common/pokCore/autogenerated/pokApiClient';
import {
  newQuestion,
  convertQuestion,
} from '../../../common/pokCore/contexts/SurveyContext';
import ValidationAlert from '../../../app/components/ValidationAlert';
import { useNotifications } from '../../../common/hooks/useNotifications';
import { usePokCore } from '../../../common/hooks/usePokCore';
import { SurveyEditorTabs } from '../../containers/SurveyEditorView/SurveyEditorView';
import { responseErrors } from '../../../common/pokCore/validation/responseErrors';

import { SurveyQuestionsGrid } from './SurveyQuestionsGrid';
import { SurveyQuestionsControls } from './SurveyQuestionsControls';
import { QuestionEditor } from './QuestionEditor';

interface SurveyQuestionsProps {
  survey?: GetSurveyDto;
  surveyId?: string;
  setNotSavedTab: (tab: SurveyEditorTabs | undefined) => void;
  setActiveTab: Dispatch<SetStateAction<string>>;
  refreshSurvey: () => void;
  isLocked?: boolean;
}

export const SurveyQuestions: React.FC<SurveyQuestionsProps> = props => {
  const [questions, setQuestions] = useState<CreateUpdateQuestionDto[]>([]);
  const [originalQuestions, setOriginalQuestions] = useState<
    CreateUpdateQuestionDto[]
  >([]);
  const [showSaveErrors, setShowSaveErrors] = useState(false);
  const [saveErrors, setSaveErrors] = useState<string[]>([]);
  const [editingQuestion, setEditingQuestion] =
    useState<CreateUpdateQuestionDto | null>(null);
  const [editingIndex, setEditingIndex] = useState<number | null>(null);
  const [showEditor, setShowEditor] = useState(false);

  const pok = usePokCore();
  const notifications = useNotifications();

  useEffect(() => {
    if (props.survey?.questions) {
      const convertedQuestions = props.survey.questions.map(q =>
        convertQuestion(q),
      );
      setQuestions(convertedQuestions);
      setOriginalQuestions(convertedQuestions);
    }
  }, [props.survey]);

  const checkForChanges = (updatedQuestions: CreateUpdateQuestionDto[]) => {
    const dataChanged = !isEqual(updatedQuestions, originalQuestions);
    props.setNotSavedTab(dataChanged ? SurveyEditorTabs.QUESTIONS : undefined);
  };

  const handleAdd = () => {
    if (!props.surveyId || props.isLocked) {
      return;
    }
    const newOrder = questions.length;
    setEditingQuestion(newQuestion(props.surveyId, newOrder));
    setEditingIndex(null);
    setShowEditor(true);
  };

  const handleEdit = (index: number) => {
    setEditingQuestion(questions[index]);
    setEditingIndex(index);
    setShowEditor(true);
  };

  const handleSaveQuestion = (question: CreateUpdateQuestionDto) => {
    let updatedQuestions: CreateUpdateQuestionDto[];
    if (editingIndex !== null) {
      updatedQuestions = [...questions];
      updatedQuestions[editingIndex] = {
        ...question,
        order: questions[editingIndex].order,
      };
    } else {
      updatedQuestions = [...questions, question];
    }
    setQuestions(updatedQuestions);
    checkForChanges(updatedQuestions);
    setShowEditor(false);
    setEditingQuestion(null);
    setEditingIndex(null);
  };

  const handleDelete = (index: number) => {
    const updatedQuestions = questions
      .filter((_, i) => i !== index)
      .map((q, i) => ({ ...q, order: i }));
    setQuestions(updatedQuestions);
    checkForChanges(updatedQuestions);
  };

  const handleMoveUp = (index: number) => {
    if (index === 0) {
      return;
    }
    const updatedQuestions = [...questions];
    [updatedQuestions[index - 1], updatedQuestions[index]] = [
      updatedQuestions[index],
      updatedQuestions[index - 1],
    ];
    updatedQuestions.forEach((q, i) => {
      q.order = i;
    });
    setQuestions(updatedQuestions);
    checkForChanges(updatedQuestions);
  };

  const handleMoveDown = (index: number) => {
    if (index === questions.length - 1) {
      return;
    }
    const updatedQuestions = [...questions];
    [updatedQuestions[index], updatedQuestions[index + 1]] = [
      updatedQuestions[index + 1],
      updatedQuestions[index],
    ];
    updatedQuestions.forEach((q, i) => {
      q.order = i;
    });
    setQuestions(updatedQuestions);
    checkForChanges(updatedQuestions);
  };

  const handleSave = async () => {
    if (!props.surveyId) {
      return;
    }

    // Wyczyść stare błędy przed zapisem
    setSaveErrors([]);
    setShowSaveErrors(false);

    const questionsWithOrder = questions.map((q, index) => ({
      ...q,
      order: index,
    }));

    const result = await saveQuestions(questionsWithOrder);

    if (!result.saved) {
      setSaveErrors(result.errors);
      setShowSaveErrors(true);
      return;
    }

    setOriginalQuestions(questionsWithOrder);
    props.setNotSavedTab(undefined);
    props.refreshSurvey();
    notifications.saveCompleted();
  };

  const saveQuestions = async (questions: CreateUpdateQuestionDto[]) => {
    if (!props.surveyId) {
      return {
        saved: false,
        errors: [],
      };
    }

    try {
      await pok.surveys.updateQuestions(props.surveyId, questions);
      return {
        saved: true,
        errors: [],
      };
    } catch (response) {
      return {
        saved: false,
        errors: await responseErrors(response as ResponseError),
      };
    }
  };

  return (
    <>
      <Form className="d-grid gap-4 mb-3">
        <SurveyQuestionsGrid
          questions={questions}
          onEdit={handleEdit}
          onDelete={handleDelete}
          onMoveUp={handleMoveUp}
          onMoveDown={handleMoveDown}
          isLocked={props.isLocked}
        />
        <ValidationAlert
          show={showSaveErrors}
          errors={saveErrors}
          className="m-3"
        />
        <SurveyQuestionsControls
          onAdd={handleAdd}
          onSave={handleSave}
          isLocked={props.isLocked}
        />
      </Form>

      {showEditor && editingQuestion && (
        <QuestionEditor
          show={showEditor}
          question={editingQuestion}
          onSave={handleSaveQuestion}
          onClose={() => {
            setShowEditor(false);
            setEditingQuestion(null);
            setEditingIndex(null);
          }}
        />
      )}
    </>
  );
};
